<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import type { ReportColumn } from '../../models/column.types'

import { Checkbox, Drawer, MetricView } from '@/shared/ui'

const open = defineModel<boolean>({ default: false })
const props = withDefaults(
  defineProps<{
    columns: ReportColumn[]
    selectedDimensions: ReportColumn[]
    selectedMetrics: ReportColumn[]
    requiredFields?: string[]
    title: string
    dimensionsLabel: string
    metricsLabel: string
    closeLabel: string
  }>(),
  { requiredFields: () => [] },
)

const emit = defineEmits<{
  apply: [settings: { dimensions: ReportColumn[]; metrics: ReportColumn[] }]
}>()

const selected = ref(new Set<string>())
const dimensions = computed(() => props.columns.filter((column) => !column.numeric))
const metrics = computed(() => props.columns.filter((column) => column.numeric))

watch(
  open,
  (isOpen) => {
    if (!isOpen) return
    selected.value = new Set([
      ...props.requiredFields,
      ...props.selectedDimensions.map((column) => column.field),
      ...props.selectedMetrics.map((column) => column.field),
    ])
  },
  { immediate: true },
)

function isSelected(field: string): boolean {
  return selected.value.has(field)
}

function setSelected(field: string, value: boolean): void {
  const next = new Set(selected.value)
  if (value) next.add(field)
  else if (!props.requiredFields.includes(field)) next.delete(field)
  selected.value = next
  emit('apply', {
    dimensions: dimensions.value.filter((column) => next.has(column.field)),
    metrics: metrics.value.filter((column) => next.has(column.field)),
  })
}
</script>

<template>
  <Drawer v-model="open" :title="title" :close-label="closeLabel" size="md">
    <div class="column-customize-drawer__groups grid gap-md">
      <fieldset
        class="column-customize-drawer__group min-w-0 m-0 p-0 overflow-hidden border border-border rounded-md"
      >
        <legend>{{ dimensionsLabel }}</legend>
        <ul class="column-customize-drawer__list m-0 p-0 list-none">
          <li v-for="column in dimensions" :key="column.field">
            <Checkbox
              :model-value="isSelected(column.field)"
              :aria-label="column.header"
              :disabled="requiredFields.includes(column.field)"
              tone="accent"
              @update:model-value="setSelected(column.field, $event)"
            />
            <MetricView :column="column" :size="20" />
          </li>
        </ul>
      </fieldset>
      <fieldset
        class="column-customize-drawer__group min-w-0 m-0 p-0 overflow-hidden border border-border rounded-md"
      >
        <legend>{{ metricsLabel }}</legend>
        <ul class="column-customize-drawer__list m-0 p-0 list-none">
          <li v-for="column in metrics" :key="column.field">
            <Checkbox
              :model-value="isSelected(column.field)"
              :aria-label="column.header"
              tone="accent"
              @update:model-value="setSelected(column.field, $event)"
            />
            <MetricView :column="column" :size="20" />
          </li>
        </ul>
      </fieldset>
    </div>
  </Drawer>
</template>

<style scoped>
.column-customize-drawer__group legend {
  width: 100%;
  padding-block: var(--ui-space-sm);
  padding-inline: var(--ui-space-md);
  border-bottom: 1px solid var(--ui-color-border);
  background-color: var(--ui-color-surface-sunken);
  color: var(--ui-color-fg);
  font-size: 0.875rem;
  font-weight: 600;
}
.column-customize-drawer__list li {
  display: flex;
  min-height: 2.75rem;
  align-items: center;
  gap: var(--ui-space-xs);
  padding-block: var(--ui-space-sm);
  padding-inline: var(--ui-space-md);
}
.column-customize-drawer__list li + li {
  border-top: 1px solid var(--ui-color-border);
}
</style>
